import { Empty, Input, ModalProps, Spin, Typography, notification } from 'antd'
import { debounce } from 'lodash-es'
import { FunctionComponent, RefObject, useCallback, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useToggle } from 'react-use'
import styled from 'styled-components'
import {
  useAddKolsToKolCollectionMutation,
  useCreateKolCollectionMutation,
  useDeleteKolsFromKolCollectionMutation,
  useFetchKolCollectionInfiniteQuery,
} from '@/api/workspace-api/kol-collections-api'
import AddKolsToCollectionLabel from '@/components/common/add-kols-to-collection-label'
import { BasicModal } from '@/components/common/antd/basic-modal'
import { useCollectionSearchEventAt } from '@/components/common/contexts/collection-search-event-at-context'
import RemoveKolsFromCollectionLabel from '@/components/common/remove-kols-from-collection-label'
import useResetCurrentFootprintPage from '@/components/kol/common/kol-cooperation-detail-drawer/footprint-history/use-reset-current-footprint-page'
import { VipOnlyModal } from '@/components/modal/vip-only-modal'
import SafeNewTabLink from '@/components/safe-new-tab-link'
import { Page } from '@/hooks/use-authorization/constants'
import useCollectionData from '@/hooks/use-collection-data'
import useCollectionLimit from '@/hooks/use-collection-limit'
import useHandleInputComposition from '@/hooks/use-handle-input-composition'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Nullable } from '@/types/nullable'
import {
  type FetchKolCollectionsInfiniteRequest,
  type KolCollectionsCollection,
} from '@/types/schema/api/kol-collections'
import { AddToProjectProperties, ampli } from '@/utils/ampli'
import { Potential } from '@/utils/detail/potential'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

const { Search } = Input

export interface AddKolsToCollectionModalProps
  extends Pick<ModalProps, 'open'> {
  kols: {
    id: string
    dcIdHashed?: string
    name: string
    potential?: Nullable<Potential>
    customizedTagsNames: string[]
    isCollectionKol: boolean
  }[]
  from: AddToProjectProperties['from']
  isClickedFromDrawer?: AddToProjectProperties['isClickedFromDrawer']
  onCollectedChange?: (kolId: string, isCollected: boolean) => void
  platform?: AddToProjectProperties['platform']
  containerRef?: RefObject<HTMLElement>
  onClose: VoidFunction
}

const INITIAL_COLLECTION_ID = 0
const DEBOUNCE_TIME = 500

const AddKolsToCollectionModal: FunctionComponent<
  AddKolsToCollectionModalProps
> = ({
  kols,
  from,
  isClickedFromDrawer,
  open,
  onCollectedChange,
  onClose,
  platform,
  containerRef,
}) => {
  const { compositionEnd, handleInputComposition } = useHandleInputComposition()
  const { formatMessage } = useIntl()
  const { withFreePermission } = usePermissions()
  const { at } = useCollectionSearchEventAt()
  const [ctaVisible, setCtaVisible] = useToggle(false)
  const [isCreatingCollection, setIsCreatingCollection] = useToggle(false)

  const isSingleKol = kols.length === 1

  const [debouncedRequest, setDebouncedRequest] =
    useState<FetchKolCollectionsInfiniteRequest>({
      name: '',
      kol_uuid: isSingleKol ? kols[0].id : undefined,
    })
  const resetCurrentFootprintPage = useResetCurrentFootprintPage()

  const [addKolsToKolCollection, { isLoading: isAddingKolsToKolCollection }] =
    useAddKolsToKolCollectionMutation()
  const [
    deleteKolsFromKolCollection,
    { isLoading: isDeletingKolsFromKolCollection },
  ] = useDeleteKolsFromKolCollectionMutation()

  const { isNoCollectionData } = useCollectionData()

  const {
    currentData,
    isFetching: isFetchingCollections,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useFetchKolCollectionInfiniteQuery(debouncedRequest)
  const [createKolCollection] = useCreateKolCollectionMutation()
  const { limit, isCollectionLimitReached, limited } = useCollectionLimit()
  const initialCollection: KolCollectionsCollection = {
    id: INITIAL_COLLECTION_ID,
    name: formatMessage({ id: 'collection:untitled_collection' }),
    last_updated: '',
    owner_id: 0,
    owner_name: '',
    kol_amount: 0,
    query_collected_kol: null,
  }

  const renderList: KolCollectionsCollection[] = isNoCollectionData
    ? [initialCollection]
    : (currentData?.pages
        .map((page) => {
          return page.data
        })
        .flat() ?? [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOnSearch = useCallback(
    debounce((keyword: string) => {
      setDebouncedRequest({
        name: keyword,
        kol_uuid: isSingleKol ? kols[0]?.id : undefined,
      })
      ampli.searchForCollection({
        collectionType: 'KOL',
        at,
      })
    }, DEBOUNCE_TIME),
    [at, isSingleKol],
  )

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isFetchingCollections,
    hasNextPage,
    onLoadMore: toVoidFunction(fetchNextPage),
    rootMargin: '0px 0px 0px 0px',
  })

  const createCollectionAndAdd = async (name: string): Promise<void> => {
    if (isCreatingCollection) {
      return
    }

    if (isCollectionLimitReached) {
      setCtaVisible(true)
      return
    }

    if (name.length < 2 || name.length > 50) {
      notification.warning({
        message: `Warning`,
        description: formatMessage({ id: 'error:project_name_fail' }),
        placement: 'bottomRight',
      })
      return
    }

    setIsCreatingCollection(true)

    try {
      const response = await createKolCollection({
        name,
        uuid: isSingleKol ? kols[0].id : undefined,
      }).unwrap()

      ampli.createCollection({
        collectionType: 'KOL',
        collectionId: response.id,
        collectionName: name,
        isDefault: isNoCollectionData,
      })

      await addToCollection(response.id, name)

      refetch()
    } catch (error) {
      logger.error(error)
    } finally {
      setIsCreatingCollection(false)
    }
  }

  const handleCloseCta: VoidFunction = () => setCtaVisible(false)

  const addToCollection = async (
    id: number,
    collectionName: string,
  ): Promise<void> => {
    try {
      await addKolsToKolCollection({
        id,
        kol_ids: kols.map((kol) => kol.id),
      }).unwrap()

      refetch()
      onCollectedChange?.(kols[0].id, true)
      if (isClickedFromDrawer) {
        resetCurrentFootprintPage()
      }

      kols.forEach((kol) => {
        ampli.addToProject({
          collectionId: id,
          collectionName,
          kolId: kol.id,
          kolDcId: kol.dcIdHashed,
          kolName: kol.name,
          kolPotential: [kol.potential === Potential.High],
          kolCategory: kol.customizedTagsNames,
          from,
          collectionType: 'KOL',
          platform,
          isClickedFromDrawer,
          isMultiple: kols.length > 1,
        })
      })
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({ id: 'general:operation_failure' }),
        placement: 'bottomRight',
      })
    }
  }

  const handleRemoveButtonClick = async (
    collectionId: number,
    collectionKolId: string,
    isCollectionKol: boolean,
  ): Promise<void> => {
    try {
      await deleteKolsFromKolCollection({
        id: collectionId,
        collection_kol_ids: [collectionKolId],
        kol_ids: kols.map((kol) => kol.id),
        isCollectionKol,
      }).unwrap()

      refetch()

      onCollectedChange?.(kols[0].id, isCollectionKol)

      if (isClickedFromDrawer) {
        resetCurrentFootprintPage()
      }

      kols.forEach((kol) => {
        ampli.removeCollection({
          kolId: kol.id,
          kolName: kol.name,
          kolCategory: kol.customizedTagsNames,
        })
      })
    } catch (error) {
      logger.error(error)
    }
  }

  const handleAddButtonClick = (
    collectionId: number,
    collectionName: string,
    reachKolLimit: boolean,
  ): void => {
    if (reachKolLimit) {
      return
    }

    if (collectionId === INITIAL_COLLECTION_ID) {
      createCollectionAndAdd(
        formatMessage({ id: 'collection:untitled_collection' }),
      )
    } else {
      addToCollection(collectionId, collectionName)
    }
  }

  const handleClose: VoidFunction = () => {
    onClose?.()

    setDebouncedRequest((prev) => ({
      ...prev,
      name: '',
    }))
  }

  const isKolUpdating =
    isAddingKolsToKolCollection || isDeletingKolsFromKolCollection

  return (
    <StyledModal
      destroyOnClose
      footer={null}
      getContainer={containerRef?.current ?? document.body}
      open={open}
      title={formatMessage({ id: 'kol_collection:add_to_collection' })}
      onCancel={handleClose}
    >
      <Search
        {...handleInputComposition}
        enterButton={formatMessage({ id: 'project:creat_submit_btn' })}
        loading={isCreatingCollection}
        placeholder={formatMessage({ id: 'search:condition_submit_btn' })}
        onChange={(event) => {
          if (!compositionEnd) {
            return
          }

          handleOnSearch(event.target.value)
        }}
        onCompositionEnd={(event) => {
          handleInputComposition.onCompositionEnd?.(event)
          handleOnSearch(event.currentTarget.value)
        }}
        onSearch={toVoidFunction(createCollectionAndAdd)}
      />
      <CollectionsWrapper ref={rootRef}>
        {isKolUpdating && (
          <LoadingWrapper>
            <Spin />
          </LoadingWrapper>
        )}
        {!isKolUpdating && (
          <>
            {renderList.map((collection) => {
              const numberOfCollectionsHasThisKol = renderList.reduce(
                (acc, cur) => {
                  if (cur.query_collected_kol) {
                    acc += 1
                  }
                  return acc
                },
                0,
              )

              const queryCollectedKol = collection.query_collected_kol
              const inCollection = queryCollectedKol !== null

              const reachKolLimit =
                limited && collection.kol_amount >= limit.numberOfKols
              return (
                <ResourceWrapper key={collection.id}>
                  <TextWrapper>
                    <HyperRef
                      data-collection-link
                      href={`${Page.CollectionsKol}/${collection.id}`}
                    >
                      <ResourceName>{collection.name}</ResourceName>
                    </HyperRef>
                    <ResourceKOLAmount>
                      {collection.kol_amount} KOL
                    </ResourceKOLAmount>
                    {reachKolLimit && (
                      <Typography.Text type='danger'>
                        {formatMessage(
                          {
                            id: 'error:project_limit_reached_alert',
                          },
                          {
                            n: limit.numberOfKols,
                          },
                        )}
                      </Typography.Text>
                    )}
                  </TextWrapper>
                  {inCollection ? (
                    <RemoveKolsFromCollectionLabel
                      onClick={toVoidFunction(() =>
                        handleRemoveButtonClick(
                          collection.id,
                          queryCollectedKol.collection_kol_id,
                          numberOfCollectionsHasThisKol > 1,
                        ),
                      )}
                    />
                  ) : (
                    <AddKolsToCollectionLabel
                      onClick={() =>
                        handleAddButtonClick(
                          collection.id,
                          collection.name,
                          reachKolLimit,
                        )
                      }
                    />
                  )}
                </ResourceWrapper>
              )
            })}
            {(isFetchingCollections || hasNextPage) && (
              <LoadingWrapper ref={sentryRef}>
                <Spin />
              </LoadingWrapper>
            )}
          </>
        )}
        {!isNoCollectionData &&
          renderList.length === 0 &&
          !isFetchingCollections && (
            <Empty
              description={formatMessage({
                id: 'autoreport:chart_label_empty_data',
              })}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
      </CollectionsWrapper>
      <VipOnlyModal
        canContinueBrowsingFromProp={!withFreePermission}
        source='Collection Count Limit'
        title={formatMessage(
          { id: 'error:project_reach_upper_limit' },
          {
            count: limit.numberOfCollections,
          },
        )}
        visible={ctaVisible}
        onClose={handleCloseCta}
      />
    </StyledModal>
  )
}

const StyledModal = styled(BasicModal)`
  text-align: left;

  .ant-input,
  .ant-btn {
    height: 32px;
  }
`

const HyperRef = styled(SafeNewTabLink)`
  > span {
    &:hover {
      color: ${({ theme }): string => theme.colors.brand.primaryHover};
    }
  }
`

const CollectionsWrapper = styled.div`
  margin: 16px 0 0 0;
  padding: 0 0 24px 0;
  height: 360px;
  overflow-y: scroll;

  .ant-divider-horizontal {
    margin: 16px 0;
  }
`

const LoadingWrapper = styled.div`
  padding: 24px 0;
  display: flex;
  justify-content: center;
`

const ResourceName = styled.span`
  font-size: 16px;
  line-height: 22px;
  color: ${({ theme }): string => theme.colors.textPrimary};
  max-width: 350px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const ResourceKOLAmount = styled.div`
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }): string => theme.colors.gray6};
`

const TextWrapper = styled.div`
  margin: 0 20px 0 0;
`

const ResourceWrapper = styled.div`
  padding: 16px 8px;
  border-bottom: 1px solid ${({ theme }): string => theme.colors.gray4};
  display: flex;
  justify-content: space-between;
`

export default AddKolsToCollectionModal
